import React from 'react';
import { 
  Card, CardContent, Typography, List, ListItem, ListItemIcon, 
  ListItemText, Box, Divider 
} from '@mui/material';
import FiberManualRecordIcon from '@mui/icons-material/FiberManualRecord';
import SentimentSatisfiedAltIcon from '@mui/icons-material/SentimentSatisfiedAlt';
import SentimentVeryDissatisfiedIcon from '@mui/icons-material/SentimentVeryDissatisfied';
import SentimentDissatisfiedIcon from '@mui/icons-material/SentimentDissatisfied';
import SentimentVerySatisfiedIcon from '@mui/icons-material/SentimentVerySatisfied';
import HotelIcon from '@mui/icons-material/Hotel';
import NightlightIcon from '@mui/icons-material/Nightlight';

const BudENotes = ({ moodTrends, usageSummary, productEngagement }) => {
  // Add null/undefined checks with default values
  const moodTrendsData = moodTrends || {};
  const usageSummaryData = usageSummary || {};
  const productEngagementData = productEngagement || {};

  const moodTags = moodTrendsData["Top 3 mood tags"] || [];
  const latestMood = moodTrendsData["Latest mood submitted"] || "N/A";

  return (
  <Box
  sx={{
    flex: 1,
    overflowY: "auto",
    pr: 1,
    transition: "margin 0.3s ease",
    scrollbarWidth: "thin",
    scrollbarColor: "rgb(180, 179, 222) transparent",
    "&::-webkit-scrollbar": { width: "4px", height: "0.5px" },
    "&::-webkit-scrollbar-thumb": {
      backgroundColor:"rgb(171, 171, 181)",
      borderRadius: "10px",
    },
    "&::-webkit-scrollbar-track": { backgroundColor: "transparent" },

    // 👇 this part is important
    height: "100%",         // fill available parent height
    maxHeight: "100%",      // keep it constrained
    display: "flex",
    flexDirection: "column",
    gap: 2,
    p: 2,
  }}
>
      <Typography
        variant="h6"
        textAlign="center"
        color="primary"
        fontWeight="bold"
        gutterBottom
      >
        Bud-i Notes
      </Typography>

      {/* Mood Trends */}
      <StyledCard title="Mood Trends">
        <Typography variant="body2" color="text.secondary" gutterBottom>
          Top 3 mood tags:
        </Typography>
        <List dense>
          {moodTags && moodTags.length > 0 ? (
            moodTags.map((tag, i) => (
              <ListItem key={i} disableGutters>
                <ListItemIcon sx={{ minWidth: 24 }}>
                  <FiberManualRecordIcon sx={{ fontSize: 8, color: "primary.main" }} />
                </ListItemIcon>
                <ListItemText
                  primary={tag}
                  primaryTypographyProps={{ variant: "body2", fontWeight: 500 }}
                />
              </ListItem>
            ))
          ) : (
            <Typography variant="body2" color="text.secondary">No mood data available</Typography>
          )}
        </List>
        <Divider sx={{ my: 1 }} />
        <Typography variant="body2">
          Latest mood submitted:{" "}
          <Box component="span" sx={{ verticalAlign: "middle" }}>
            {getMoodIcon(latestMood)}
          </Box>{" "}
          <strong>{latestMood}</strong>
        </Typography>
      </StyledCard>

      {/* Usage Summary */}
      <StyledCard title="Usage Summary">
        <List dense>
          {[
            `Total sessions: ${usageSummaryData["Total sessions"] || "N/A"}`,
            `Total messages: ${usageSummaryData["Total messages"] || "N/A"}`,
            `Average messages per session: ${usageSummaryData["Average messages per session"] || "N/A"}`,
            `Longest session: ${usageSummaryData["Longest session"] || "N/A"}`
          ].map((item, i) => (
            <ListItem key={i} disableGutters>
              <ListItemIcon sx={{ minWidth: 24 }}>
                <FiberManualRecordIcon sx={{ fontSize: 8, color: "primary.main" }} />
              </ListItemIcon>
              <ListItemText
                primary={item}
                primaryTypographyProps={{ variant: "body2" }}
              />
            </ListItem>
          ))}
        </List>
      </StyledCard>

      {/* Product Engagement */}
      <StyledCard title="Product Engagement">
        <List dense>
          {[
            `Total product recommendations: ${productEngagementData["Total product recommendations"] || "N/A"}`,
            `Most common intent: ${productEngagementData["Most common intent"] || "N/A"}`,
            `Sessions with recommendations: ${productEngagementData["Sessions with recommendations"] || "N/A"}`
          ].map((item, i) => (
            <ListItem key={i} disableGutters>
              <ListItemIcon sx={{ minWidth: 24 }}>
                <FiberManualRecordIcon sx={{ fontSize: 8, color: "primary.main" }} />
              </ListItemIcon>
              <ListItemText
                primary={item}
                primaryTypographyProps={{ variant: "body2" }}
              />
            </ListItem>
          ))}
        </List>
      </StyledCard>
    </Box>
  );
};

// Reusable styled card component
const StyledCard = ({ title, children }) => (
  // <Card 
  //   variant="outlined" 
  //   sx={{ borderRadius: 2, boxShadow: 2, flexShrink: 0  }}
  // >
      <Card sx={{ boxShadow: 0.5, borderRadius: 2,flexShrink: 0, p: 1 }}>
    <CardContent>
      <Typography variant="subtitle1" color="primary" fontWeight="bold" gutterBottom>
        {title}
      </Typography>
      {children}
    </CardContent>
  </Card>
);

// Function to map mood to Material UI icons
const getMoodIcon = (mood) => {
  const moodMap = {
    "Calm": <SentimentSatisfiedAltIcon color="warning" fontSize="small" />,
    "Sleepy": <HotelIcon color="primary" fontSize="small" />,
    "Anxious": <SentimentDissatisfiedIcon sx={{ color: '#ea4335' }} fontSize="small" />,
    "Relaxed": <SentimentVerySatisfiedIcon sx={{ color: '#0f9d58' }} fontSize="small" />,
    "Stressed": <SentimentVeryDissatisfiedIcon sx={{ color: '#ea4335' }} fontSize="small" />,
    "Can't Sleep": <NightlightIcon color="primary" fontSize="small" />
  };
  return moodMap[mood] || <SentimentSatisfiedAltIcon fontSize="small" />;
};

export default BudENotes;
